import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Edit as EditIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { userService } from '../../services/user.service';
import { UserProfile as UserProfileType } from '../../types/user.types';
import { PageHeader } from '../ui/PageHeader';
import { SurfaceCard } from '../ui/SurfaceCard';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { ProfileStravaCard } from './ProfileStravaCard';
import { LanguageSelect } from '../layout/LanguageSelect';
import { formatDate } from '../../i18n/locale-format';

interface EditableField {
  displayName: string;
  email: string;
  bio: string;
}

export const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('profile');
  const [profile, setProfile] = useState<UserProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const [editForm, setEditForm] = useState<EditableField>({
    displayName: '',
    email: '',
    bio: ''
  });

  useEffect(() => {
    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await userService.getCurrentUserProfile();
      setProfile(response.data);
      setEditForm({
        displayName: response.data.displayName || '',
        email: response.data.email || '',
        bio: response.data.bio || ''
      });
    } catch (err: any) {
      setError(t('loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = () => {
    setEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    if (profile) {
      setEditForm({
        displayName: profile.displayName || '',
        email: profile.email || '',
        bio: profile.bio || ''
      });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Email is the sign-in identity and cannot be changed, so it is never sent
      const { email, ...changes } = editForm;
      const response = await userService.updateUserProfile(changes);
      setProfile(response.data);
      setEditing(false);
      setSuccess(t('messages.updated'));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || t('messages.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: keyof EditableField, value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConnectStrava = () => {
    window.location.href = '/connect-strava';
  };

  const handleChangePassword = () => {
    navigate('/profile/change-password');
  };

  const handleSyncStrava = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSyncResult(null);

      const response = await userService.syncStravaActivities();
      const { syncedCount, skippedCount, activities } = response.data;

      const syncedNames = activities?.synced?.slice(0, 3).map(a => a.name).join(', ') || '';
      const moreCount = activities?.synced && activities.synced.length > 3
        ? ` ${t('sync.andMore', { count: activities.synced.length - 3 })}`
        : '';

      let resultMessage = t('sync.synced', { count: syncedCount });
      if (skippedCount > 0) {
        resultMessage += `, ${t('sync.skipped', { count: skippedCount })}`;
      }
      if (syncedCount > 0 && syncedNames) {
        resultMessage += `: ${syncedNames}${moreCount}`;
      }
      if (syncedCount === 0 && skippedCount === 0) {
        resultMessage = t('sync.none');
      }

      setSyncResult(resultMessage);

      setTimeout(() => setSyncResult(null), 10000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || t('messages.syncFailed');
      const retryAfter = err.response?.data?.retryAfter;

      if (retryAfter) {
        const minutes = Math.ceil(retryAfter / 60);
        setError(t('messages.retryWait', { error: errorMessage, count: minutes }));
      } else {
        setError(errorMessage);
      }

      setTimeout(() => setError(null), 10000);
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnectStrava = async () => {
    try {
      setDisconnecting(true);
      setError(null);
      setSuccess(null);

      const response = await userService.disconnectStrava();

      // Refresh profile to reflect disconnected state
      await loadProfile();

      if (response.data.warning) {
        setSuccess(t('messages.disconnectedWithWarning'));
      } else {
        setSuccess(t('messages.disconnected'));
      }

      setDisconnectDialogOpen(false);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || t('messages.disconnectFailed');
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('loadFailed')}</Alert>
      </Box>
    );
  }

  const joined = new Date(profile.createdAt);
  const joinedLabel = Number.isNaN(joined.getTime())
    ? ''
    : ` · ${t('joined', { date: formatDate(joined, { month: 'short', year: 'numeric' }) })}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader title={t('page.title')} subtitle={t('page.subtitle')} />

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
      {syncResult && <Alert severity="success">{syncResult}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(260px, 1fr) minmax(0, 2fr)' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <SurfaceCard bodyPadding="22px">
            <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'center' }}>
              <InitialsAvatar name={profile.displayName || profile.username} size={60} gradient />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.375, minWidth: 0 }}>
                <Box sx={{ fontSize: 18, fontWeight: 600, overflowWrap: 'anywhere' }}>{profile.displayName}</Box>
                <Box sx={{ fontSize: 13, color: 'var(--muted)', overflowWrap: 'anywhere' }}>
                  @{profile.username}{joinedLabel}
                </Box>
              </Box>
            </Box>
          </SurfaceCard>

          <ProfileStravaCard
            stravaProfile={profile.stravaProfile}
            syncing={syncing}
            onSync={handleSyncStrava}
            onDisconnect={() => setDisconnectDialogOpen(true)}
            onConnect={handleConnectStrava}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <SurfaceCard
            title={t('personalInfo.title')}
            subtitle={t('personalInfo.subtitle')}
            action={!editing && (
              <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={handleStartEdit}>
                {t('personalInfo.editProfile')}
              </Button>
            )}
            bodyPadding={0}
          >
            <Box
              sx={{
                px: '22px',
                pb: '18px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
                gap: 1.75,
              }}
            >
              <TextField
                fullWidth
                label={t('personalInfo.displayName')}
                value={editing ? editForm.displayName : profile.displayName}
                onChange={(e) => handleFieldChange('displayName', e.target.value)}
                disabled={!editing}
                helperText={t('personalInfo.displayNameHelp')}
              />
              <TextField
                fullWidth
                label={t('personalInfo.username')}
                value={profile.username}
                disabled
                helperText={t('personalInfo.usernameHelp')}
              />
              <TextField
                fullWidth
                label={t('personalInfo.email')}
                type="email"
                value={profile.email || ''}
                disabled
                helperText={t('personalInfo.emailHelp')}
                sx={{ gridColumn: '1 / -1' }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('personalInfo.bio')}
                value={editing ? editForm.bio : (profile.bio || '')}
                onChange={(e) => handleFieldChange('bio', e.target.value)}
                disabled={!editing}
                placeholder={t('personalInfo.bioPlaceholder')}
                sx={{ gridColumn: '1 / -1' }}
              />
            </Box>
            {editing && (
              <Box sx={{ px: '22px', py: 1.75, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button variant="outlined" onClick={handleCancelEdit} disabled={saving}>
                  {t('common:actions.cancel')}
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? t('common:actions.saving') : t('personalInfo.saveChanges')}
                </Button>
              </Box>
            )}
          </SurfaceCard>

          <SurfaceCard bodyPadding="18px 22px">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ fontSize: 15, fontWeight: 600 }}>{t('password.title')}</Box>
                <Box sx={{ fontSize: 13, color: 'var(--muted)' }}>
                  {t('password.hint')}
                </Box>
              </Box>
              <Button variant="outlined" startIcon={<LockIcon />} onClick={handleChangePassword}>
                {t('password.change')}
              </Button>
            </Box>
          </SurfaceCard>

          <SurfaceCard bodyPadding="18px 22px">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ fontSize: 15, fontWeight: 600 }}>{t('common:language.label')}</Box>
                <Box sx={{ fontSize: 13, color: 'var(--muted)' }}>{t('common:language.hint')}</Box>
              </Box>
              <LanguageSelect />
            </Box>
          </SurfaceCard>
        </Box>
      </Box>

      {/* Disconnect Strava Confirmation Dialog */}
      <Dialog
        open={disconnectDialogOpen}
        onClose={() => setDisconnectDialogOpen(false)}
        aria-labelledby="disconnect-dialog-title"
        aria-describedby="disconnect-dialog-description"
      >
        <DialogTitle id="disconnect-dialog-title">
          {t('disconnectDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="disconnect-dialog-description">
            {t('disconnectDialog.body')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDisconnectDialogOpen(false)}
            disabled={disconnecting}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleDisconnectStrava}
            color="error"
            variant="contained"
            disabled={disconnecting}
          >
            {disconnecting ? t('disconnectDialog.disconnecting') : t('disconnectDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
